/* eslint-disable camelcase */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Button, Spin, Switch, Skeleton } from 'antd';
import { BellOutlined, LoadingOutlined } from '@ant-design/icons';
import { useInView } from 'react-intersection-observer';
import { Link, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import styled, { css } from 'styled-components';
import {
  useGetAllNotificationData,
  useUpdateAllMarkAsRead,
  useUpdateMarkAsRead
} from '../../../services/Notification/hooks';

const LoaderWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px;
  max-height: 500px;
  min-height: 500px;
`;

const SpinnerWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
`;

const NotificationWrapper = styled(Row)`
  padding-top: 16px;
  margin: -4px;
  max-height: 500px;
  ${(props) =>
    props.isLoading &&
    css`
      min-height: 500px;
    `}
  overflow-y: scroll;
  .header {
    padding-inline: 16px;
    .header-content {
      display: flex;
      align-items: center;
    }
    .header-title {
      font-size: 20px;
      font-weight: 700;
    }
  }
  .switch {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #5c5c5c;
    font-weight: 500;
  }
  .item-wrapper {
    background: #f6f6f6;
    padding: 12px 24px;
    border-bottom: 1px solid #d3d3d3;
    &.read {
      background: #fff;
    }
    .icon-wrapper {
      svg {
        color: #fc8543;
      }
    }
    .feature-name {
      margin: 0px;
      font-size: 14px;
      font-weight: 700;
    }
    .description {
      margin: 0px;
      font-size: 14px;
      font-weight: 400;
    }
    .d-date-time {
      color: #5c5c5c;
      font-size: 14px;
      font-weight: 400;
    }
    .noti-type {
      margin: 0;
      font-size: 12px;
      font-weight: 500;
      color: #5c5c5c;
      line-height: 16px;
      span {
        margin-inline: 5px;
        font-size: 24px;
        font-weight: 700;
      }
    }
  }
  .mark-button {
    color: #fc8543;
    .button-span {
      text-decoration: underline;
      font-size: 14px;
    }
  }
`;

const ICON_MAP = {
  Rfi: (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="36" height="36" rx="18" fill="#FFF6F0" />
      <path
        d="M20.3335 7.5V12.1667C20.3335 12.4761 20.4564 12.7728 20.6752 12.9916C20.894 13.2104 21.1907 13.3333 21.5002 13.3333H26.1668"
        stroke="#FC8543"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M23.8335 28.5H12.1668C11.548 28.5 10.9545 28.2542 10.5169 27.8166C10.0793 27.379 9.8335 26.7855 9.8335 26.1667V9.83333C9.8335 9.21449 10.0793 8.621 10.5169 8.18342C10.9545 7.74583 11.548 7.5 12.1668 7.5H20.3335L26.1668 13.3333V26.1667C26.1668 26.7855 25.921 27.379 25.4834 27.8166C25.0458 28.2542 24.4523 28.5 23.8335 28.5Z"
        stroke="#FC8543"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <g clipPath="url(#clip0_11934_135960)">
        <path
          d="M12.9082 24.7674V20.3354H14.2207C14.5055 20.3354 14.7787 20.4668 14.98 20.7005C15.1814 20.9343 15.2946 21.2514 15.2946 21.5819C15.2946 21.9125 15.1814 22.2296 14.98 22.4633C14.7787 22.6971 14.5055 22.8284 14.2207 22.8284H12.9082"
          stroke="#FC8543"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.1016 22.8284L15.2947 24.7674"
          stroke="#FC8543"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M20.069 20.335H17.6826V24.7669"
          stroke="#FC8543"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M17.6826 22.551H19.5917"
          stroke="#FC8543"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M22.4561 20.335V24.7669"
          stroke="#FC8543"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_11934_135960">
          <rect width="11.6667" height="7" fill="white" transform="translate(12.1665 19.1667)" />
        </clipPath>
      </defs>
    </svg>
  ),
  Submittal: (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="36" height="36" rx="18" fill="#FFF6F0" />
      <g clipPath="url(#clip0_11934_135971)">
        <path
          d="M20.3335 7.5V12.1667C20.3335 12.4761 20.4564 12.7728 20.6752 12.9916C20.894 13.2104 21.1907 13.3333 21.5002 13.3333H26.1668"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.8335 18V9.83333C9.8335 9.21449 10.0793 8.621 10.5169 8.18342C10.9545 7.74583 11.548 7.5 12.1668 7.5H20.3335L26.1668 13.3333V18"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.5 18H28.5"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10.3689 29.3155C10.8199 29.3155 11.1856 28.9498 11.1856 28.4988C11.1856 28.0478 10.8199 27.6821 10.3689 27.6821C9.91788 27.6821 9.55225 28.0478 9.55225 28.4988C9.55225 28.9498 9.91788 29.3155 10.3689 29.3155Z"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M25.4822 22.3216C25.9332 22.3216 26.2989 21.9559 26.2989 21.5049C26.2989 21.0539 25.9332 20.6882 25.4822 20.6882C25.0312 20.6882 24.6655 21.0539 24.6655 21.5049C24.6655 21.9559 25.0312 22.3216 25.4822 22.3216Z"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M13.6674 28.4027H22.1018C22.553 28.4027 22.9858 28.2235 23.3048 27.9044C23.6239 27.5853 23.8032 27.1526 23.8032 26.7013C23.8032 26.2501 23.6239 25.8173 23.3048 25.4983C22.9858 25.1792 22.553 24.9999 22.1018 24.9999H13.8987C13.4474 24.9999 13.0147 24.8207 12.6956 24.5016C12.3765 24.1825 12.1973 23.7498 12.1973 23.2986C12.1973 22.8473 12.3765 22.4146 12.6956 22.0955C13.0147 21.7764 13.4474 21.5972 13.8987 21.5972L22.1018 21.5972"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_11934_135971">
          <rect width="28" height="28" fill="white" transform="translate(4 4)" />
        </clipPath>
      </defs>
    </svg>
  ),
  DailyLog: (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="36" height="36" rx="18" fill="#FFF6F0" />
      <g clipPath="url(#clip0_11934_136049)">
        <path
          d="M21.5 28.5H11C10.0717 28.5 9.1815 28.1313 8.52513 27.4749C7.86875 26.8185 7.5 25.9283 7.5 25V23.8333H19.1667V26.1667C19.1667 26.7855 19.4125 27.379 19.8501 27.8166C20.2877 28.2542 20.8812 28.5 21.5 28.5ZM21.5 28.5C22.1188 28.5 22.7123 28.2542 23.1499 27.8166C23.5875 27.379 23.8333 26.7855 23.8333 26.1667V9.83333C23.8333 9.37184 23.9702 8.92072 24.2266 8.537C24.483 8.15329 24.8474 7.85422 25.2737 7.67762C25.7001 7.50101 26.1693 7.4548 26.6219 7.54484C27.0745 7.63487 27.4903 7.8571 27.8166 8.18342C28.1429 8.50974 28.3651 8.9255 28.4552 9.37812C28.5452 9.83075 28.499 10.2999 28.3224 10.7263C28.1458 11.1526 27.8467 11.517 27.463 11.7734C27.0793 12.0298 26.6282 12.1667 26.1667 12.1667H23.8333L21.5 28.5ZM26.1667 7.5H13.3333C12.4051 7.5 11.5148 7.86875 10.8585 8.52513C10.2021 9.1815 9.83333 10.0717 9.83333 11V23.8333"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.5 12.1667H19.1667"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.5 16.8333H19.1667"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_11934_136049">
          <rect width="28" height="28" fill="white" transform="translate(4 4)" />
        </clipPath>
      </defs>
    </svg>
  ),
  PunchList: (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="36" height="36" rx="18" fill="#FFF6F0" />
      <g clipPath="url(#clip0_11934_135994)">
        <path
          d="M21.4998 9.25L16.8332 13.9167L12.1665 15.6667L10.4165 17.4167L18.5832 25.5833L20.3332 23.8333L22.0832 19.1667L26.7498 14.5"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.5 21.5L9.25 26.75"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M20.9165 8.66675L27.3332 15.0834"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_11934_135994">
          <rect width="28" height="28" fill="white" transform="translate(4 4)" />
        </clipPath>
      </defs>
    </svg>
  ),
  ActionItem: (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="36" height="36" rx="18" fill="#FFF6F0" />
      <g clipPath="url(#clip0_12134_3888)">
        <path
          d="M14.5002 9.83325H12.1668C11.548 9.83325 10.9545 10.0791 10.5169 10.5167C10.0793 10.9543 9.8335 11.5477 9.8335 12.1666V26.1666C9.8335 26.7854 10.0793 27.3789 10.5169 27.8165C10.9545 28.2541 11.548 28.4999 12.1668 28.4999H23.8335C24.4523 28.4999 25.0458 28.2541 25.4834 27.8165C25.921 27.3789 26.1668 26.7854 26.1668 26.1666V12.1666C26.1668 11.5477 25.921 10.9543 25.4834 10.5167C25.0458 10.0791 24.4523 9.83325 23.8335 9.83325H21.5002"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M19.1667 7.5H16.8333C15.5447 7.5 14.5 8.54467 14.5 9.83333C14.5 11.122 15.5447 12.1667 16.8333 12.1667H19.1667C20.4553 12.1667 21.5 11.122 21.5 9.83333C21.5 8.54467 20.4553 7.5 19.1667 7.5Z"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M13.1909 17.55L14.8568 17.55"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M13.1909 23.2454L14.8568 23.2454"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M18 17.031L19.1667 18.1977L21.9613 15.4031"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M18 22.7261L19.1667 23.8928L21.9613 21.0981"
          stroke="#FC8543"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_12134_3888">
          <rect width="28" height="28" fill="white" transform="translate(4 4)" />
        </clipPath>
      </defs>
    </svg>
  )
};

const NotificationList = ({ refetchCount, setOpen, isNewCount }) => {
  const { ref, inView } = useInView();
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const [filters, setFilters] = useState({ show_unread: false });
  const userDetails = Cookies.get('userInfo');
  const userInfo = (userDetails && JSON.parse(userDetails)) || {};
  const { data, isLoading, refetch, fetchNextPage, isFetchingNextPage } = useGetAllNotificationData(
    userInfo.unique_reference_id,
    filters
  );
  const { mutate: markAllAsRead } = useUpdateAllMarkAsRead();
  const { mutate: markAsRead } = useUpdateMarkAsRead();
  const handleMarkAllAsRead = () => {
    markAllAsRead({}, { onSuccess: () => Promise.all([refetch(), refetchCount()]) });
  };
  const handleMarkAsRead = (id) => {
    markAsRead({ id }, { onSuccess: () => Promise.all([refetch(), refetchCount()]) });
  };
  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);
  useEffect(() => {
    if (isNewCount) {
      if (wrapperRef?.current) {
        wrapperRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      }
      refetch();
    }
  }, [isNewCount]);
  const loader = useMemo(
    () => (
      <Spin
        indicator={
          <LoadingOutlined
            style={{
              fontSize: 24
            }}
            spin
          />
        }
      />
    ),
    []
  );
  const handleLinkClick = () => {
    setOpen(false);
    navigate(
      `/${userInfo?.company?.unique_reference_id}/notification/${userInfo.notification_preference_id}/settings`
    );
  };
  // if (isLoading) {
  //   return <LoaderWrapper>{loader}</LoaderWrapper>;
  // }
  const { pages = [] } = data || {};
  const lastIntex = pages.length - 1;
  const lastPageData = pages?.[lastIntex];
  const { pagination = {} } = lastPageData || {};
  const dataAvailable = !!lastPageData?.data?.data?.length;
  return (
    <NotificationWrapper wrap isLoading={isLoading}>
      <Col span={24} ref={wrapperRef}>
        <Row justify="space-between" align="middle" className="header">
          <div className="header-content">
            <h2 className="header-title">Notification</h2>
            <div onClick={() => handleLinkClick()} style={{ cursor: 'pointer' }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none">
                <path
                  d="M18.7228 7.13923C18.5048 6.88449 18.35 6.58187 18.271 6.25601C17.7448 4.08686 14.6591 4.08686 14.1329 6.25601C13.7919 7.65804 12.191 8.32262 10.9557 7.57157C9.04847 6.41042 6.86697 8.59315 8.02813 10.4992C8.20268 10.7855 8.30716 11.1089 8.33308 11.4432C8.359 11.7775 8.30562 12.1132 8.17729 12.4229C8.04895 12.7327 7.84929 13.0078 7.59455 13.2258C7.33981 13.4438 7.03719 13.5986 6.71133 13.6775C4.54219 14.2038 4.54219 17.2895 6.71133 17.8157C7.0369 17.8949 7.3392 18.0498 7.59365 18.2678C7.8481 18.4857 8.04753 18.7607 8.17571 19.0703C8.3039 19.3799 8.35723 19.7153 8.33137 20.0494C8.30551 20.3834 8.2012 20.7067 8.0269 20.9928C6.86574 22.9001 9.04847 25.0816 10.9545 23.9204C11.2408 23.7459 11.5642 23.6414 11.8985 23.6155C12.2328 23.5896 12.5685 23.6429 12.8782 23.7713C13.188 23.8996 13.4631 24.0993 13.6811 24.354C13.8991 24.6088 14.0539 24.9114 14.1329 25.2372C14.6591 27.4064 17.7448 27.4064 18.271 25.2372C18.3502 24.9117 18.5051 24.6094 18.7231 24.3549C18.9411 24.1005 19.216 23.901 19.5256 23.7729C19.8352 23.6447 20.1706 23.5913 20.5047 23.6172C20.8387 23.6431 21.162 23.7474 21.4482 23.9217C23.3554 25.0828 25.5369 22.9001 24.3758 20.9941C24.2012 20.7078 24.0967 20.3843 24.0708 20.0501C24.0449 19.7158 24.0983 19.3801 24.2266 19.0703C24.3549 18.7606 24.5546 18.4855 24.8093 18.2675C25.0641 18.0494 25.3667 17.8947 25.6926 17.8157C27.8617 17.2895 27.8617 14.2038 25.6926 13.6775C25.367 13.5984 25.0647 13.4435 24.8102 13.2255"
                  stroke="#FC8543"
                  strokeWidth="2"
                  strokeLinecap="square"
                  strokeLinejoin="round"
                />
                <path
                  d="M16.2014 19.4524C18.2481 19.4524 19.9073 17.7933 19.9073 15.7466C19.9073 13.6999 18.2481 12.0408 16.2014 12.0408C14.1548 12.0408 12.4956 13.6999 12.4956 15.7466C12.4956 17.7933 14.1548 19.4524 16.2014 19.4524Z"
                  stroke="#FC8543"
                  strokeWidth="2"
                  strokeLinecap="square"
                  strokeLinejoin="round"
                />
                <path
                  d="M21.2055 10.7426L27.3193 4.62891"
                  stroke="#FC8543"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22.084 4.62891H27.3192L27.3192 9.6146"
                  stroke="#FC8543"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          {(dataAvailable || filters.show_unread) && (
            <span className="switch">
              Only Unread
              <Switch
                style={{ marginLeft: 16 }}
                checked={filters.show_unread}
                size="small"
                onChange={(checked) => setFilters({ show_unread: checked })}
              />
            </span>
          )}
        </Row>
        <Row style={{ width: '100%' }} align="middle" justify="end">
          {dataAvailable && (
            <div>
              <Button type="link" className="mark-button" onClick={handleMarkAllAsRead}>
                <span className="button-span">Mark all as read</span>
              </Button>
            </div>
          )}
        </Row>
      </Col>
      {!isLoading &&
        pages?.map((page, i) => (
          <React.Fragment key={i}>
            {page?.data?.data?.map((item) => (
              <Col
                key={item.id}
                span={24}
                className={`item-wrapper ${item.mark_as_read ? 'read' : ''}`}>
                <Row style={{ width: '100%' }}>
                  <Col span={3} className="icon-wrapper">
                    {ICON_MAP?.[item.notifiable_type] ? (
                      ICON_MAP[item.notifiable_type]
                    ) : (
                      <BellOutlined size={36} />
                    )}
                  </Col>
                  <Col span={21}>
                    <Link to={item.notify_link} target="_blank">
                      <h3 className="feature-name">{item.description}</h3>
                    </Link>
                    <p className="description">{item.feature_name}</p>
                    {item.project_name && (
                      <p className="description">Project: {item.project_name}</p>
                    )}
                    {item.delivery_date_time && (
                      <span className="d-date-time">{item.delivery_date_time}</span>
                    )}
                    <Row justify="space-between" align="middle" wrap={false}>
                      <p className="noti-type">
                        {item.notifiable_type}
                        {item.time_since_notification && <span>.</span>}
                        {item.time_since_notification || ''}
                        <span>.</span>
                        {!item.mark_as_read ? 'Unread' : ''}
                      </p>
                      {!item.mark_as_read && (
                        <Button
                          type="link"
                          style={{ color: '#FC8543' }}
                          onClick={() => handleMarkAsRead(item.id)}>
                          <span style={{ textDecoration: 'underline', fontSize: 14 }}>
                            Mark as read
                          </span>
                        </Button>
                      )}
                    </Row>
                  </Col>
                </Row>
              </Col>
            ))}
          </React.Fragment>
        ))}
      {!dataAvailable && !isLoading && (
        <Col span={24}>
          <div
            style={{
              width: '100%',
              padding: '20px',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              display: 'flex'
            }}>
            <svg
              width="166"
              height="144"
              viewBox="0 0 166 144"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_12160_8007)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2.16209 111.627C2.80409 111.4 3.48209 111.097 4.00409 110.663C4.62209 110.145 4.87409 109.479 5.04209 108.771C5.25809 107.866 5.34809 106.902 5.61209 105.998C5.70809 105.659 5.90009 105.534 5.97809 105.48C6.18209 105.337 6.38609 105.302 6.57809 105.314C6.80609 105.332 7.12409 105.415 7.32809 105.796C7.35809 105.849 7.39409 105.932 7.42409 106.045C7.44209 106.129 7.45409 106.391 7.47209 106.498C7.52009 106.765 7.56209 107.027 7.60409 107.295C7.73609 108.182 7.80809 108.937 8.22209 109.752C8.78009 110.859 9.33809 111.537 10.1001 111.835C10.8321 112.126 11.7141 112.073 12.8361 111.841C12.9441 111.817 13.0461 111.793 13.1541 111.775C13.6461 111.692 14.1201 112.013 14.2161 112.501C14.3121 112.989 14.0001 113.465 13.5081 113.572C13.4061 113.596 13.3041 113.614 13.2081 113.632C11.6901 114.006 9.93809 115.333 8.91809 116.494C8.60609 116.851 8.14409 117.856 7.67609 118.493C7.32809 118.963 6.93809 119.278 6.61409 119.385C6.39209 119.457 6.21209 119.451 6.05609 119.409C5.83409 119.355 5.64809 119.236 5.51009 119.046C5.43209 118.945 5.36009 118.802 5.33009 118.624C5.31209 118.54 5.31209 118.32 5.31209 118.225C5.21609 117.898 5.10209 117.582 5.01209 117.249C4.80809 116.464 4.41209 115.97 3.93809 115.31C3.49409 114.697 3.02009 114.31 2.31809 114.001C2.22809 113.977 1.49609 113.798 1.23809 113.697C0.860091 113.548 0.680091 113.298 0.614091 113.162C0.500091 112.93 0.494091 112.733 0.512091 112.567C0.542091 112.317 0.656091 112.108 0.854091 111.942C0.974091 111.835 1.16009 111.734 1.40609 111.686C1.59809 111.644 2.09609 111.627 2.16809 111.621L2.16209 111.627ZM6.46409 110.347C6.50009 110.425 6.53609 110.496 6.57209 110.573C7.38809 112.192 8.30609 113.096 9.41609 113.536L9.45209 113.548C8.70809 114.096 8.03609 114.709 7.52009 115.298C7.31009 115.542 7.02809 116.041 6.72209 116.559C6.44609 115.672 5.99609 115.042 5.43209 114.256C5.00009 113.655 4.54409 113.203 3.98609 112.84C4.41809 112.62 4.83209 112.364 5.19209 112.061C5.79209 111.561 6.18809 110.978 6.45809 110.347H6.46409Z"
                  fill="#FC8543"
                />
                <path
                  d="M18.4999 7.1399C20.4881 7.1399 22.0999 5.54158 22.0999 3.56995C22.0999 1.59832 20.4881 0 18.4999 0C16.5117 0 14.8999 1.59832 14.8999 3.56995C14.8999 5.54158 16.5117 7.1399 18.4999 7.1399Z"
                  fill="#FC8543"
                />
                <path
                  d="M161.9 81.5139C163.888 81.5139 165.5 79.9156 165.5 77.944C165.5 75.9723 163.888 74.374 161.9 74.374C159.912 74.374 158.3 75.9723 158.3 77.944C158.3 79.9156 159.912 81.5139 161.9 81.5139Z"
                  fill="#FC8543"
                />
                <path
                  d="M77.9 135.658C115.676 135.658 146.3 105.29 146.3 67.8291C146.3 30.3684 115.676 0 77.9 0C40.124 0 9.5 30.3684 9.5 67.8291C9.5 105.29 40.124 135.658 77.9 135.658Z"
                  fill="#FFEBE0"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M77.6001 26.7746C85.0581 26.7746 91.1001 20.7831 91.1001 13.3873C91.1001 5.99157 85.0581 0 77.6001 0C70.1421 0 64.1001 5.99157 64.1001 13.3873C64.1001 20.7831 70.1421 26.7746 77.6001 26.7746ZM77.3001 20.8247C81.2781 20.8247 84.5001 17.6296 84.5001 13.6848C84.5001 9.74002 81.2781 6.54491 77.3001 6.54491C73.3221 6.54491 70.1001 9.74002 70.1001 13.6848C70.1001 17.6296 73.3221 20.8247 77.3001 20.8247Z"
                  fill="#3B3B3C"
                />
                <path
                  d="M77.3299 120.843C91.9279 120.843 103.76 109.11 103.76 94.6335C103.76 80.1573 91.9279 68.4241 77.3299 68.4241C62.7319 68.4241 50.8999 80.1573 50.8999 94.6335C50.8999 109.11 62.7319 120.843 77.3299 120.843Z"
                  fill="#909090"
                />
                <path
                  d="M119.162 75.1475V55.0427C119.162 44.077 114.77 33.5576 106.946 25.7989C99.1218 18.0461 88.5198 13.6848 77.4558 13.6848C66.3918 13.6848 55.7898 18.0402 47.9658 25.7989C40.1418 33.5516 35.7498 44.0711 35.7498 55.0427V75.1475C35.7498 79.7646 34.4298 84.2925 31.9458 88.2016C29.4618 92.1107 25.9158 95.2404 21.7098 97.2336C20.7438 97.6917 19.9278 98.4117 19.3578 99.3042C18.7878 100.203 18.4878 101.244 18.4878 102.303V102.41C18.4878 102.791 18.5598 103.172 18.7098 103.529C18.8598 103.886 19.0758 104.207 19.3458 104.475C19.6158 104.742 19.9458 104.963 20.2998 105.105C20.6538 105.254 21.0378 105.326 21.4278 105.326H133.46C133.844 105.326 134.228 105.248 134.588 105.105C134.948 104.963 135.272 104.742 135.542 104.475C135.812 104.207 136.034 103.88 136.178 103.529C136.328 103.172 136.4 102.797 136.4 102.41V102.303C136.4 101.244 136.1 100.209 135.53 99.3161C134.96 98.4236 134.15 97.7036 133.184 97.2515C128.978 95.2582 125.426 92.1226 122.942 88.2135C120.458 84.3044 119.144 79.7706 119.144 75.1534L119.162 75.1475Z"
                  fill="url(#paint0_linear_12160_8007)"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M136.7 105.314H18.5V101.744H136.7V105.314Z"
                  fill="#3B3B3C"
                />
                <path
                  d="M110.9 51.7643C120.176 51.7643 127.7 44.3031 127.7 35.1045C127.7 25.9059 120.176 18.4447 110.9 18.4447C101.624 18.4447 94.1001 25.9059 94.1001 35.1045C94.1001 44.3031 101.624 51.7643 110.9 51.7643Z"
                  fill="white"
                />
                <path
                  d="M106.7 35.0986C106.7 33.623 107.042 32.4568 107.72 31.6119C108.41 30.767 109.472 30.3446 110.9 30.3446C112.328 30.3446 113.372 30.773 114.062 31.6238C114.752 32.4687 115.1 33.629 115.1 35.0986C115.1 36.5682 114.752 37.7404 114.062 38.5971C113.372 39.442 112.316 39.8645 110.9 39.8645C109.484 39.8645 108.41 39.442 107.72 38.5971C107.036 37.7404 106.7 36.5801 106.7 35.0986ZM112.034 35.0986C112.034 34.4144 111.962 33.9027 111.812 33.5635C111.662 33.2125 111.356 33.0399 110.9 33.0399C110.444 33.0399 110.126 33.2125 109.976 33.5635C109.826 33.9027 109.754 34.4203 109.754 35.0986C109.754 35.7769 109.826 36.3124 109.976 36.6634C110.126 37.0026 110.432 37.1751 110.9 37.1751C111.368 37.1751 111.662 37.0026 111.812 36.6634C111.962 36.3124 112.034 35.7947 112.034 35.0986Z"
                  fill="#3B3B3C"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M44.9659 114.417C42.0919 116.993 39.2239 119.522 36.4279 122.182C35.8219 122.753 35.8039 123.693 36.3739 124.282C36.9499 124.883 37.8979 124.901 38.4979 124.33C41.2819 121.688 44.1259 119.183 46.9759 116.624C47.5879 116.071 47.6359 115.125 47.0899 114.524C46.5319 113.917 45.5839 113.864 44.9659 114.417Z"
                  fill="#3B3B3C"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M109.892 114.417C112.748 116.993 115.598 119.522 118.382 122.182C118.982 122.753 119 123.693 118.424 124.282C117.848 124.877 116.894 124.895 116.3 124.33C113.534 121.688 110.708 119.183 107.87 116.624C107.258 116.071 107.216 115.131 107.768 114.524C108.326 113.917 109.28 113.864 109.892 114.423V114.417Z"
                  fill="#3B3B3C"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M75.59 131.077C75.542 134.914 75.464 138.705 75.536 142.536C75.548 143.363 76.226 144.012 77.054 144C77.882 143.988 78.548 143.304 78.53 142.489C78.464 138.675 78.542 134.914 78.59 131.107C78.596 130.285 77.936 129.613 77.114 129.601C76.286 129.601 75.602 130.256 75.596 131.077H75.59Z"
                  fill="#3B3B3C"
                />
              </g>
              <defs>
                <linearGradient
                  id="paint0_linear_12160_8007"
                  x1="78.2838"
                  y1="103.112"
                  x2="53.1939"
                  y2="28.7229"
                  gradientUnits="userSpaceOnUse">
                  <stop stopColor="#D85B16" />
                  <stop offset="1" stopColor="#FC8543" />
                </linearGradient>
                <clipPath id="clip0_12160_8007">
                  <rect width="165" height="144" fill="white" transform="translate(0.5)" />
                </clipPath>
              </defs>
            </svg>
            <h2 style={{ color: '#080808', fontSize: '20px' }}>No Notification Yet</h2>
            <p style={{ color: '#909090', fontSize: '14px', margin: 0 }}>
              You have no Notification right now.
            </p>
          </div>
        </Col>
      )}
      {isLoading && (
        <div style={{ paddingInline: 16, width: '100%' }}>
          <Skeleton active avatar paragraph />
          <Skeleton active avatar paragraph />
          <Skeleton active avatar paragraph />
        </div>
      )}
      <Col span={24}>
        {dataAvailable && pagination.currentPage !== pagination.totalPages && (
          <div style={{ borderBottom: '1px solid transparent' }} ref={ref} />
        )}
      </Col>
      {isFetchingNextPage && (
        <div style={{ padding: 16, width: '100%' }}>
          <Skeleton active avatar paragraph />
          <Skeleton active avatar paragraph />
          <Skeleton active avatar paragraph />
        </div>
      )}
    </NotificationWrapper>
  );
};

export default NotificationList;
